import _ from 'lodash';
import {
  InteractionStepResponse,
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
  ChatTaskContextType,
  InteractionStepType,
  DisplayStage,
  StepParamsForType,
} from '@scale/shared/types/chat';
import { MultiTurnThreadHistory } from '../ThreadHistory';
import { StepWithResponse } from '../ThreadHistory.interface';
import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Button,
  Divider,
  Tab,
  Typography,
  SelectMenu,
  Banner,
  SelectMenuOption,
} from '@scale/scaleui';
import copy from 'copy-to-clipboard';
import { useDebounce } from 'react-use-v13';
import { ReviewLevel } from '@scale/shared/defaults';
import {
  setResponseForIndexWithPropagation,
  useMultiTurnInteractionValidation,
  getForcedStaticStepParams,
  DEBOUNCE_MILLISECONDS,
} from '../utils';
import { withErrorBoundary } from 'react-scale/containers/global/errorBoundary';
import {
  validateChatResponse,
  interactionStepTriggersPropagation,
} from '@scale/shared/chat/response';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ResponseTemplateStep, ResponseTemplateVariable } from './ResponseTemplateCreatorStep';
import { useStore } from 'zustand';
import { ChatStoreContextProvider, useCreateChatStore } from '../../Chat/hooks/ChatStore';
import { ChatInteraction } from '../ChatInteraction';
import { useChatLayoutStore, ChatLayout } from '../hooks/useChatLayoutStore';
import { ChatTaskParamsStages } from '@scale/shared/chat/steps';
import { StoreRef } from '../../Chat/hooks/types';
import InitialMessagesTemplateCreator from './InitialMessagesTemplateCreator';
import {
  BenchmarkTutorialResponse,
  IndexedBenchmarkTutorialStepResponse,
  isSupportedTutorialStepType,
} from '@scale/shared/types/benchmarks/tutorial';
import { isStepVisible } from '@scale/shared/chat/api';
import { displayAnswerKeyEditing } from './utils';
import { getStepDescriptorByType } from '../definitions';
import { assertNever } from '@scale/shared/types/utils';

export const ChatInteractionWithErrorBoundary = withErrorBoundary({
  reloadable: true,
})(
  ({
    chatStore,
    context,
    readOnly,
    displayStage,
  }: {
    chatStore: StoreRef;
    context: ChatTaskContextType;
    readOnly?: boolean;
    displayStage?: DisplayStage;
  }) => {
    return (
      <ChatStoreContextProvider store={chatStore}>
        <ChatTaskContext.Provider value={context}>
          <ChatInteraction readOnly={readOnly} enableLayoutOverride displayStage={displayStage} />
        </ChatTaskContext.Provider>
      </ChatStoreContextProvider>
    );
  },
);

export interface ResponseTemplateCreatorProps {
  // Populated preview config
  config: MultiTurnInteractionConfiguration;
  // Context for the chat task
  context: ChatTaskContextType;
  // If provided, templateResponses will reset to these responses if empty
  resetTemplateResponses?: IndexedInteractionStepResponse[];
  // Callback to populate template responses with variables for previewing
  populateResponses?: (
    responses: IndexedInteractionStepResponse[],
  ) => Promise<IndexedInteractionStepResponse[]>;
  // Variables that can be used in templated responses
  templateVariables?: ResponseTemplateVariable[];
  // The reviewLevel we are previewing for the reponse we are templating
  previewReviewLevel: ReviewLevel;
  // Whether or not the preview should be read only
  previewReadOnly?: boolean;
}

interface ResponseTemplateCreatorOriginalProps extends ResponseTemplateCreatorProps {
  // Unpopulated template responses to modify
  templateResponses: IndexedInteractionStepResponse[] | null;
  setTemplateResponses: (responses: IndexedInteractionStepResponse[]) => void;
  clearResponses?: () => void;
  isAnswerKeyResponse?: boolean;
  reviewLevelOptions?: { id: string; name: string }[];
  viewTurn?: string;
  setViewTurn?: (turn: string) => void;
  viewStep?: string;
  setViewStep?: (step: string) => void;
  tutorialResponses?: BenchmarkTutorialResponse[];
  setTutorialResponses?: (tutorialResponses: { responses: BenchmarkTutorialResponse[] }) => void;
}

function ResponseTemplateCreator({
  config: _config,
  context,
  templateResponses,
  setTemplateResponses,
  resetTemplateResponses,
  populateResponses,
  templateVariables,
  previewReviewLevel: _previewReviewLevel,
  previewReadOnly = true,
  isAnswerKeyResponse,
  viewTurn = '-1',
  setViewTurn,
  viewStep = '-1',
  setViewStep,
  tutorialResponses,
  setTutorialResponses,
}: ResponseTemplateCreatorOriginalProps) {
  const { setLayout } = useChatLayoutStore();

  const originalResponses = useMemo<IndexedInteractionStepResponse[] | null>(
    () => _.cloneDeep(templateResponses),
    [templateResponses],
  );

  const config: MultiTurnInteractionConfiguration = useMemo(() => {
    return {
      ..._config,
      before: _config.before.map(p => {
        return getForcedStaticStepParams(p);
      }),
      turn: _config.turn.map(p => {
        return getForcedStaticStepParams(p);
      }),
      after: _config.after.map(p => {
        return getForcedStaticStepParams(p);
      }),
    };
  }, [_config]);

  const chatStore = useCreateChatStore({
    config,
    reviewLevel: _previewReviewLevel,
    responses: _.cloneDeep(templateResponses ?? []),
  });

  const previewConfig = useStore(chatStore.current, state => state.config);
  const previewReviewLevel = useStore(chatStore.current, state => state.reviewLevel);
  const setPreviewReviewLevel = useStore(chatStore.current, state => state.setReviewLevel);
  const setChatStoreConfig = useStore(chatStore.current, state => state.setConfig);
  const previewResponses = useStore(chatStore.current, state => state.responses);
  const _setPreviewResponses = useStore(chatStore.current, state => state.setResponses);
  const setPreviewResponses = useCallback(
    async (responses: IndexedInteractionStepResponse[] | null) => {
      if (!responses) {
        _setPreviewResponses([]);
        return;
      }
      if (populateResponses) {
        const populated = await populateResponses(responses);
        _setPreviewResponses(populated);
      } else {
        _setPreviewResponses(responses);
      }
    },
    [_setPreviewResponses, populateResponses],
  );

  const maybeFallbackToResetResponses = () => {
    if (!originalResponses && resetTemplateResponses) {
      setTemplateResponses(_.cloneDeep(resetTemplateResponses));
      setPreviewResponses(_.cloneDeep(resetTemplateResponses));
    }
  };

  useDebounce(
    () => {
      // Sync previewed responses with edited responses
      setPreviewResponses(_.cloneDeep(templateResponses));
    },
    DEBOUNCE_MILLISECONDS,
    [templateResponses, templateVariables],
  );

  // This debounce is a hack since resetTemplateResponse is iteratively generated.
  // If resetTemplateResponse was explicitly set once, we could just rely on the call in the initial effect
  useDebounce(
    () => {
      maybeFallbackToResetResponses();
    },
    DEBOUNCE_MILLISECONDS,
    [resetTemplateResponses],
  );

  useEffect(() => {
    // Call setPreviewResponses on mount to populate template if needed
    setPreviewResponses(_.cloneDeep(templateResponses));
    // Call maybeFallbackToResetResponses to initialize templateResponses to resetTemplateResponse if empty
    maybeFallbackToResetResponses();
    // NOTE: Use only once when component is mounted
    setLayout(ChatLayout.Inline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setChatStoreConfig(config);
  }, [config, setChatStoreConfig]);

  useEffect(() => {
    setPreviewReviewLevel(_previewReviewLevel);
  }, [_previewReviewLevel, setPreviewReviewLevel]);

  const history = useMemo(
    () => new MultiTurnThreadHistory(config, templateResponses ?? [], previewReviewLevel),
    [config, templateResponses, previewReviewLevel],
  );
  const stepsWithResponses = useMemo(() => history.getStepsWithResponses(), [history]);
  const flattenedStepsWithResponses = useMemo(
    () =>
      history.flattenStepsWithResponses(
        stepsWithResponses.before,
        stepsWithResponses.turns,
        stepsWithResponses.after,
      ),
    [history, stepsWithResponses],
  );
  const beforeStepsLength = useMemo(() => config.before.length, [config]);
  const afterStepsLength = useMemo(() => config.after.length, [config]);
  const turnStepsLength = useMemo(
    () => flattenedStepsWithResponses.length - (beforeStepsLength + afterStepsLength),
    [flattenedStepsWithResponses, beforeStepsLength, afterStepsLength],
  );

  const DEFAULT_TUTORIAL_MESSAGES: BenchmarkTutorialResponse[] = flattenedStepsWithResponses
    .map(([_, response]) => response)
    .filter((response): response is IndexedBenchmarkTutorialStepResponse => {
      if (!response) {
        return false;
      }
      const { type } = response;
      return isSupportedTutorialStepType(type);
    })
    .map(response => {
      switch (response.type) {
        case InteractionStepType.TextCollection:
          return {
            step_id: response.step_id ?? '',
            index: response.index,
            type: InteractionStepType.TextCollection,
            fieldInstructions: {},
          };
        case InteractionStepType.TextCollectionPerResponse:
          return {
            step_id: response.step_id ?? '',
            index: response.index,
            type: InteractionStepType.TextCollectionPerResponse,
            candidates: {},
          };
        case InteractionStepType.ModelResponseSelector:
          return {
            step_id: response.step_id ?? '',
            index: response.index,
            type: InteractionStepType.ModelResponseSelector,
            stepInstructions: {},
          };
        case InteractionStepType.QualityMeasurement:
          return {
            step_id: response.step_id ?? '',
            index: response.index,
            type: InteractionStepType.QualityMeasurement,
            fieldInstructions: {},
          };
        default:
          return assertNever(response);
      }
    });

  useEffect(() => {
    if (!tutorialResponses && setTutorialResponses) {
      setTutorialResponses({ responses: DEFAULT_TUTORIAL_MESSAGES });
    }
  }, []);

  const setResponseForIndex = useCallback(
    async (response: InteractionStepResponse, index: number) => {
      const stepParams = flattenedStepsWithResponses[index]?.[0];
      const stepType = stepParams?.type;
      const step = getStepDescriptorByType(stepType);
      if (stepParams) {
        // Ensure the response is consistent itself and with previous responses
        step?.ensureResponseConsistency(stepParams, history.until(index), response);
      }
      // Propagate response changes to downstream steps
      const propagatedResponses = setResponseForIndexWithPropagation(
        config,
        response,
        index,
        templateResponses!,
      );
      setTemplateResponses(propagatedResponses);
    },
    [flattenedStepsWithResponses, history, templateResponses, setTemplateResponses, config],
  );

  const updateEditedResponse = useCallback(
    (stepIndex: number, path: string, value: any) => {
      const newStepResponse = _.cloneDeep(templateResponses![stepIndex]);
      _.set(newStepResponse, path, value);
      setResponseForIndex(newStepResponse, stepIndex);
    },
    [templateResponses, setResponseForIndex],
  );

  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [activePreviewTab, setActivePreviewTab] = useState<number>(0);
  const showCustomizableSteps = !!isAnswerKeyResponse;

  const handleCopyToClipboard = useCallback(() => {
    copy(JSON.stringify(templateResponses, null, 2));
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  }, [templateResponses]);

  const { canSubmit } = useMultiTurnInteractionValidation({
    config: previewConfig,
    responses: previewResponses,
    isSubmitting: false,
    isLoading: false,
    hasResponseLoadingError: false,
    workerComments: [],
    reviewLevel: previewReviewLevel,
    allStepsCompleted: true,
  });

  const { valid, message } = useMemo(() => {
    if (previewResponses && canSubmit) {
      try {
        return validateChatResponse(previewConfig, previewResponses, {
          includePerResponseValidation: true,
        });
      } catch (e) {
        return { valid: false, message: e.message };
      }
    } else return { valid: true, message: '' };
  }, [previewConfig, previewResponses, canSubmit]);

  const noTemplatableSteps = useMemo(() => {
    return _.every(
      flattenedStepsWithResponses.map(([stepParams, response], stepIndex) => {
        if (!stepParams || !response) {
          return true;
        }
        if (
          !isStepVisible(stepParams, previewReviewLevel) &&
          !interactionStepTriggersPropagation(response)
        ) {
          // Restrict displayed templatable fields based on Preview Review Level
          return true;
        }
        const step = getStepDescriptorByType(stepParams.type)!;
        const templatePaths = step.getResponseTemplatePaths(stepParams, history.until(stepIndex));
        return templatePaths.length === 0;
      }),
    );
  }, [flattenedStepsWithResponses, previewReviewLevel, history]);

  const getDisplayStage = () => {
    if (viewTurn === '-1') {
      if (viewStep === '-1' || viewStep === '-2') {
        return undefined;
      } else {
        const numericalViewStep = Number(viewStep);
        if (numericalViewStep < beforeStepsLength) {
          return { stage: ChatTaskParamsStages.Before };
        } else if (numericalViewStep >= beforeStepsLength + turnStepsLength) {
          return { stage: ChatTaskParamsStages.After };
        } else {
          const turnIndex = Math.max(
            flattenedStepsWithResponses
              .slice(0, Number(viewStep) + 1)
              .reduce(
                (count, [_, response]) =>
                  response && response.type === InteractionStepType.MultiTurnContinue
                    ? count + 1
                    : count,
                0,
              ),
            0,
          );
          return { stage: ChatTaskParamsStages.Turn, turnIndex };
        }
      }
    }

    if (viewTurn === 'before') {
      return { stage: ChatTaskParamsStages.Before };
    }

    if (viewTurn === 'after') {
      return { stage: ChatTaskParamsStages.After };
    } else {
      const turnIndex = Number(viewTurn);
      return { stage: ChatTaskParamsStages.Turn, turnIndex };
    }
  };

  const getStepsFromTurn = useCallback((): StepWithResponse[] => {
    if (viewTurn === '-1') {
      return flattenedStepsWithResponses;
    }

    if (viewTurn === 'before') {
      return flattenedStepsWithResponses.slice(0, beforeStepsLength);
    }

    if (viewTurn === 'after') {
      return flattenedStepsWithResponses.slice(beforeStepsLength + turnStepsLength);
    } else {
      const numTurnIndex = Number(viewTurn);
      return _.zip(
        stepsWithResponses.turns[numTurnIndex].steps,
        stepsWithResponses.turns[numTurnIndex].responses,
      );
    }
  }, [
    viewTurn,
    flattenedStepsWithResponses,
    beforeStepsLength,
    turnStepsLength,
    stepsWithResponses,
  ]);

  const displayAnswerKeyStep = (stepParams: StepParamsForType<InteractionStepType>) => {
    if (isAnswerKeyResponse && showCustomizableSteps) {
      return displayAnswerKeyEditing(stepParams.type);
    }
    return true;
  };

  const filteredStepsFromTurn = getStepsFromTurn().filter(
    ([step, response]) => !!response && step && displayAnswerKeyStep(step),
  );

  const getStepOptions = [
    { id: '-2', name: 'All Steps for current turn' },
    { id: '-1', name: 'All Steps for current review level' },
    ...filteredStepsFromTurn.map(([step, response]) => {
      const stepName: SelectMenuOption<string> = {
        id: `${response!.index}`,
        name: `Step ${response!.index}: ${step!.title}`,
      };
      return stepName;
    }),
  ];

  return (
    <div className="flex-1 flex flex-col min-w-[1080px] gap-4">
      {!valid && (
        <Banner
          title="Response Validation Warning: Consider resetting template if config has changed."
          subtitle={message}
          variant="warning"
        />
      )}
      <Divider />
      <div className="flex gap-4">
        <div className="flex-1 flex flex-col gap-4 p-1 h-[70vh]">
          {!noTemplatableSteps && setViewStep && setViewTurn && (
            <div className="flex flex-row justify-between">
              <SelectMenu
                className="w-[80%] mr-2"
                options={[
                  { id: '-1', name: 'All Turns' },
                  { id: 'before', name: 'Before Turn' },
                  ...stepsWithResponses.turns.map((_, turnIndex) => {
                    return {
                      id: `${turnIndex}`,
                      name: `Turn ${turnIndex + 1}`,
                    };
                  }),
                  { id: 'after', name: 'After Turn' },
                ]}
                selectedOptionId={viewTurn}
                setSelected={(id: string) => {
                  setViewStep('-1');
                  setViewTurn(id);
                }}
                label="View Turn"
              />
              <SelectMenu
                className="w-[80%]"
                options={getStepOptions}
                selectedOptionId={viewStep}
                setSelected={(id: string) => {
                  setViewStep(id);
                }}
                label={'View Step'}
              />
            </div>
          )}
          <div className="overflow-y-scroll">
            {noTemplatableSteps && <InitialMessagesTemplateCreator params={_config} />}
            {filteredStepsFromTurn.map(([_stepParams, _response]) => {
              const stepParams = _stepParams!;
              const response = _response!;
              const step = getStepDescriptorByType(stepParams.type)!;

              if (viewStep !== '-1' && viewStep !== '-2' && viewStep !== `${response!.index}`) {
                return null;
              }

              if (
                !isStepVisible(stepParams, previewReviewLevel) &&
                !interactionStepTriggersPropagation(response!) &&
                viewStep === '-1'
              ) {
                // Restrict displayed templatable fields based on Preview Review Level
                return null;
              }
              const responseIndex = response.index;
              const templatePaths = step.getResponseTemplatePaths(
                stepParams,
                history.until(responseIndex),
              );
              if (templatePaths.length === 0) return null;
              return (
                <div key={`step-${stepParams.id}-${responseIndex}`} className="my-6">
                  <ResponseTemplateStep
                    responseIndex={responseIndex}
                    title={`Step ${responseIndex}: ${step.title}`}
                    subtitle={JSON.stringify(step.title)}
                    paths={templatePaths}
                    response={templateResponses![responseIndex]}
                    updateResponse={(path: string, value: any) => {
                      updateEditedResponse(responseIndex, path, value);
                    }}
                    variables={templateVariables}
                    enableMarkdown={
                      response.type === InteractionStepType.PromptInput ||
                      response.type === InteractionStepType.ModelResponseEditor ||
                      response.type === InteractionStepType.ModelResponseSelector
                    }
                    enableTutorialFeedback={
                      isAnswerKeyResponse &&
                      (stepParams.type === InteractionStepType.TextCollection ||
                        stepParams.type === InteractionStepType.TextCollectionPerResponse)
                    }
                    // TODO: map this out to variables
                    stepTutorial={
                      tutorialResponses
                        ? tutorialResponses.find(response => response.index === responseIndex)
                        : undefined
                    }
                    setStepTutorial={
                      setTutorialResponses && tutorialResponses
                        ? stepInstruction =>
                            setTutorialResponses({
                              responses: tutorialResponses.map(response =>
                                response.index === responseIndex ? stepInstruction : response,
                              ),
                            })
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="w-[60%] h-[70vh] p-1 overflow-y-scroll">
          <Tab.Group selectedIndex={activePreviewTab} onChange={setActivePreviewTab}>
            <Tab.List>
              <Tab>Task Preview</Tab>
              <Tab>JSON</Tab>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <div className="pt-2 flex flex-col">
                  <ChatInteractionWithErrorBoundary
                    chatStore={chatStore}
                    context={{
                      ...context,
                      // TODO: technically should check if it's the first review level in project, fix once we pass project pipeline to this component
                      isInitialAttempt: previewReviewLevel === ReviewLevel.Attempt,
                    }}
                    // TODO: do not ever pass false unless noTemplatableSteps is true until the issue in https://github.com/scaleapi/scaleapi/pull/74540 is resolved
                    readOnly={previewReadOnly && !noTemplatableSteps}
                    displayStage={getDisplayStage()}
                  />
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="border border-neutral-300 bg-neutral-0 border-1 rounded-md">
                  <div className="flex justify-between items-center p-4">
                    <Typography.Subtitle3>Response JSON</Typography.Subtitle3>
                    <Button variant="outline" onClick={handleCopyToClipboard}>
                      {isCopied ? 'Copied!' : 'Copy JSON to Clipboard'}
                    </Button>
                  </div>
                  <Divider />
                  <div className="p-4 whitespace-pre overflow-x-auto font-mono">
                    {JSON.stringify(templateResponses, null, 2)}
                  </div>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
}

function ErrorPlaceholderComponent() {
  return <Typography.Subtitle2>Something went wrong.</Typography.Subtitle2>;
}

export default withErrorBoundary({ reloadable: true, ErrorPlaceholderComponent })(
  ResponseTemplateCreator,
);
